import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import AuthService from '../service/auth.service';
import UserService from '../service/user.service';
import AccountService from '../service/account.service';
import { setAuthToken } from '../service/api.client';
import { isSuperAdmin } from '../utils/accountPath';

export const ApiContext = createContext({});

export const ApiProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem('token') || null);
  const [user, setUser] = useState(null);
  const [account, setAccount] = useState(null);
  const [homeAccountId, setHomeAccountId] = useState(null);

  const login = useCallback(async (email, password) => {
    const res = await AuthService.signin(email, password);
    if (res && res.accessToken) {
      const tk = res.accessToken;
      setToken(tk);
      setAuthToken(tk);
      localStorage.setItem('token', tk);
      const u = res.user || null;
      setUser(u);
      setHomeAccountId(u?.accountId ?? null);
      if (u?.accountId) {
        try {
          const acc = await AccountService.getById(u.accountId);
          setAccount(acc);
        } catch {
          setAccount(null);
        }
      } else {
        setAccount(null);
      }
    }
    return res;
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setAccount(null);
    setHomeAccountId(null);
    setAuthToken(null);
    localStorage.removeItem('token');
  }, []);

  const loadActiveAccount = useCallback(async (accountId) => {
    if (!accountId) return null;
    try {
      const acc = await AccountService.getById(accountId);
      setAccount(acc);
      return acc;
    } catch (err) {
      if (err?.status === 401) {
        logout();
        window.location.href = '/login';
      }
      throw err;
    }
  }, [logout]);

  const getUser = useCallback(async (id) => {
    try {
      const data = await UserService.getById(id);
      const fetched = data.user || data;
      setUser(fetched);
      setHomeAccountId(fetched.accountId ?? null);
      return fetched;
    } catch (err) {
      if (err && err.response && err.response.status === 401) {
        logout();
        window.location.href = '/login';
        return null;
      }
      throw err;
    }
  }, [logout]);

  const decodeToken = (tk) => {
    if (!tk) return null;
    try {
      const parts = tk.split('.');
      if (parts.length < 2) return null;
      const payload = parts[1];
      const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const json = decodeURIComponent(
        atob(b64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(json);
    } catch (e) {
      return null;
    }
  };

  useEffect(() => {
    if (!token) return;
    setAuthToken(token);
    const payload = decodeToken(token);
    const id = payload && (payload.sub || payload.id || payload.userId || payload._id);
    if (id) {
      getUser(id).catch(() => {});
    }
  }, [token, getUser]);

  const superAdmin = useMemo(() => isSuperAdmin(user), [user]);

  return (
    <ApiContext.Provider value={{
      token,
      user,
      account,
      homeAccountId,
      isSuperAdmin: superAdmin,
      login,
      logout,
      getUser,
      loadActiveAccount,
      setAccount,
    }}>
      {children}
    </ApiContext.Provider>
  );
};
